import type { CustomAttributeData } from '@risksmart-app/domain/src/types/common.types';
import type {
  Action_Status_Enum,
  InputMaybe,
  InsertChildActionMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface InsertChildActionTRPCInput {
  Title: string;
  DateDue: string;
  DateRaised: string;
  Status: Action_Status_Enum;
  ParentId?: InputMaybe<string>;
  Priority?: InputMaybe<number>;
  Description?: InputMaybe<string>;
  ClosedDate?: InputMaybe<string>;
  CustomAttributeData?: InputMaybe<CustomAttributeData>;
  OwnerUserIds: string[];
  OwnerGroupIds: string[];
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
  ViewerUserIds?: InputMaybe<string[]>;
  ViewerGroupIds?: InputMaybe<string[]>;
  TagTypeIds: string[];
  DepartmentTypeIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
function mapTrpcResponseToGraphQL(trpcData: {
  Id: string;
}): InsertChildActionMutation {
  return {
    insertChildAction: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
}

export const useInsertChildActionTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.action.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.action.actionById.queryKey(),
        });
      },
    }),
  });

  return {
    insertChildAction: async (
      variables: InsertChildActionTRPCInput
    ): Promise<InsertChildActionMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
